// Shared by the Ledger screen, its PDF/Excel export and its print (2026-09-11)
// so all three show exactly the same days, balances and totals.

export const money = (n) => Number(n || 0).toFixed(2);

export const TYPE_LABEL = {
  OPENING_BALANCE: "Opening Balance",
  VOUCHER_PAYMENT: "Voucher Payment",
  // Batch168 - unspent balance handed back; Reference is the voucher it came from.
  VOUCHER_RETURN: "Voucher Return",
  // Batch204 - money brought in for a voucher approved above the self-pay ceiling, and its
  // give-back when that voucher is cancelled. Reference = the voucher.
  VOUCHER_FUNDING: "High-value Funding (In)",
  VOUCHER_FUNDING_REV: "Funding Returned (Out)",
  REFILL_ISSUE: "Refill Issued",
  ADJUSTMENT_IN: "Adjustment (In)",
  ADJUSTMENT_OUT: "Adjustment (Out)",
};

export const dayLabel = (d) =>
  new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
export const timeLabel = (d) => new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
// Entry date for the ledger's Date column, e.g. "12 Sep, 14:32".
export const entryDateLabel = (d) =>
  new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const dateKey = (v) => new Date(v).toDateString();

// Groups the flat ledger feed into Fund -> Day sections, each day showing an
// "Opening Balance" line and a "Closing Balance" line - purely computed at
// render time from the running BalanceAfterTransaction each row already
// carries, per direct instruction not to build a real day-close process for
// this module. Credit increases the fund balance, Debit decreases it
// (confirmed against sp_app_pettycash_ledger_post's IsIncrease->Credit
// convention), so a day's opening balance is simply its first
// chronological row's BalanceAfterTransaction with that same row's own
// Credit/Debit undone: opening = balanceAfter - credit + debit. This works
// even for a fund's very first-ever entry (yields 0) and needs no extra
// day-boundary fetch - the running balance already encodes everything
// before it.
//
// Running Debit / Running Credit columns were REMOVED on 2026-09-09: users
// were reading those never-resetting cumulative totals as the day's own
// figures and misjudging the fund. Debit, Credit and Balance are what the
// screen is for. What IS shown is each day's own debit/credit total on its
// Closing Balance line, and each fund's own totals for the chosen period -
// figures that reset with the day/period and so can't be misread that way.
export const groupByFundAndDay = (rows) => {
  const sorted = [...rows].sort((a, b) => {
    if (a.fundId !== b.fundId) return a.fundId - b.fundId;
    return new Date(a.transactionDate) - new Date(b.transactionDate) || a.ledgerId - b.ledgerId;
  });

  const fundGroups = [];
  let currentFund = null;
  let currentDay = null;

  for (const row of sorted) {
    if (!currentFund || currentFund.fundId !== row.fundId) {
      currentFund = { fundId: row.fundId, fundName: row.fundName, fundCode: row.fundCode, branchCode: row.branchCode, days: [] };
      fundGroups.push(currentFund);
      currentDay = null;
    }
    const key = dateKey(row.transactionDate);
    if (!currentDay || currentDay.key !== key) {
      currentDay = { key, date: row.transactionDate, rows: [] };
      currentFund.days.push(currentDay);
    }
    currentDay.rows.push({ ...row });
  }

  fundGroups.forEach((fund) => {
    fund.days.forEach((day) => {
      const first = day.rows[0];
      const last = day.rows[day.rows.length - 1];
      day.openingBalance = Number(first.balanceAfterTransaction) - Number(first.creditAmount) + Number(first.debitAmount);
      day.closingBalance = Number(last.balanceAfterTransaction);
      day.totalDebit = day.rows.reduce((s, r) => s + Number(r.debitAmount || 0), 0);
      day.totalCredit = day.rows.reduce((s, r) => s + Number(r.creditAmount || 0), 0);
    });
    // The whole period for this fund, still in oldest-first order here.
    fund.periodOpening = fund.days[0].openingBalance;
    fund.periodClosing = fund.days[fund.days.length - 1].closingBalance;
    fund.totalDebit = fund.days.reduce((s, d) => s + d.totalDebit, 0);
    fund.totalCredit = fund.days.reduce((s, d) => s + d.totalCredit, 0);
    fund.entryCount = fund.days.reduce((s, d) => s + d.rows.length, 0);
    // Opening/closing balances above need oldest-to-newest processing order
    // to compute correctly (each day's opening carries over from the
    // previous day's closing) - only reverse for display *after* that math
    // is done, so the latest date renders first per direct request.
    fund.days.reverse();
  });

  return fundGroups;
};

// ---- Sort order (2026-09-22) ----
// "Date" keeps the Fund -> Day book with its opening / closing lines (newest or
// oldest day first). "Voucher number" and "Receipt number" are look-ups, not a
// book: the entries are listed flat in that order, per fund, without day
// sections - an opening/closing balance only means something in date order.
export const LEDGER_SORTS = [
  { value: "DATE_DESC", label: "Date - newest first" },
  { value: "DATE_ASC", label: "Date - oldest first" },
  { value: "VOUCHER", label: "Voucher number" },
  { value: "RECEIPT", label: "Receipt number" },
];
export const isFlatSort = (sortBy) => sortBy === "VOUCHER" || sortBy === "RECEIPT";

const byText = (a, b) => {
  // Blanks sink to the bottom; "PCV-000012" / "R-9" style numbers compare numerically.
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};
const byDate = (a, b) => new Date(a.transactionDate) - new Date(b.transactionDate) || a.ledgerId - b.ledgerId;

// The flat list for one fund in the chosen look-up order. Ties fall back to date.
export const sortFlatRows = (rows, sortBy) => {
  const key = sortBy === "RECEIPT" ? (r) => r.receiptNumber : (r) => (r.referenceType === "Voucher" ? r.referenceNumber : null);
  return [...rows].sort((a, b) => byText(key(a), key(b)) || byDate(a, b));
};

// Applies the sort to the grouped book: date sorts flip the day order, look-up
// sorts add `flatRows` to each fund for the flat views to draw from.
export const applyLedgerSort = (fundGroups, sortBy) =>
  fundGroups.map((fund) => {
    if (isFlatSort(sortBy)) {
      const rows = fund.days.flatMap((d) => d.rows);
      return { ...fund, flatRows: sortFlatRows(rows, sortBy) };
    }
    // groupByFundAndDay hands the days back newest-first.
    return sortBy === "DATE_ASC" ? { ...fund, days: [...fund.days].reverse() } : fund;
  });

// One line per fund for a printout's heading - the period at a glance.
export const fundPeriodLine = (fund) =>
  `${fund.fundName} (${fund.branchCode}): opening ${money(fund.periodOpening)} · debit ${money(fund.totalDebit)}` +
  ` · credit ${money(fund.totalCredit)} · closing ${money(fund.periodClosing)}`;

// ---- Excel/PDF export: the same Fund -> Day layout, flattened for listExportUtils ----
// Each day becomes one titled group (newest first, like the screen), holding
// its Opening line, its entries, and a Closing line with the day's totals.
// Shared by the Ledger page and the Petty Cash Overview (2026-09-15).
export const buildLedgerExport = (fundGroups, sortBy = "DATE_DESC") => {
  const showFundHeader = fundGroups.length > 1;
  const rows = [];
  const groupOrder = [];
  if (isFlatSort(sortBy)) {
    // Look-up order: one group per fund, entries only - no opening/closing lines.
    fundGroups.forEach((fund) => {
      const group = fund.fundName;
      groupOrder.push(group);
      (fund.flatRows || []).forEach((r) => rows.push({ group, kind: "ENTRY", r }));
    });
    return { exportRows: rows, groupOrder };
  }
  fundGroups.forEach((fund) => {
    fund.days.forEach((day) => {
      const group = showFundHeader ? `${dayLabel(day.date)}||${fund.fundName}` : dayLabel(day.date);
      groupOrder.push(group);
      rows.push({ group, kind: "OPENING", balance: day.openingBalance });
      day.rows.forEach((r) => rows.push({ group, kind: "ENTRY", r }));
      rows.push({ group, kind: "CLOSING", debit: day.totalDebit, credit: day.totalCredit, balance: day.closingBalance });
    });
  });
  return { exportRows: rows, groupOrder };
};

const amountCell = (n) => (Number(n) ? money(n) : "");
export const LEDGER_EXPORT_COLUMNS = [
  { header: "Date", value: (x) => (x.kind === "ENTRY" ? entryDateLabel(x.r.transactionDate) : "") },
  {
    header: "Description",
    value: (x) =>
      x.kind === "OPENING" ? "Opening Balance" : x.kind === "CLOSING" ? "Closing Balance (day total)" : x.r.description || "",
  },
  { header: "Debit", align: "right", value: (x) => (x.kind === "ENTRY" ? amountCell(x.r.debitAmount) : x.kind === "CLOSING" ? amountCell(x.debit) : "") },
  { header: "Credit", align: "right", value: (x) => (x.kind === "ENTRY" ? amountCell(x.r.creditAmount) : x.kind === "CLOSING" ? amountCell(x.credit) : "") },
  { header: "Balance", align: "right", value: (x) => money(x.kind === "ENTRY" ? x.r.balanceAfterTransaction : x.balance) },
  { header: "Type", value: (x) => (x.kind === "ENTRY" ? TYPE_LABEL[x.r.transactionType] || x.r.transactionType : "") },
  { header: "Reference", value: (x) => (x.kind === "ENTRY" ? x.r.referenceNumber || "" : "") },
  // Batch193 - the receipt number entered when the voucher was paid.
  { header: "Receipt No", value: (x) => (x.kind === "ENTRY" ? x.r.receiptNumber || "" : "") },
];
